/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import MultiLanguageInputErrorText from '../MultiLanguageInput/components/MultiLanguageInputErrorText/MultiLanguageInputErrorText';
import MultiLanguageTextAreaHeader from './components/MultiLanguageTextAreaHeader/MultiLanguageTextAreaHeader';
import MultiLanguageTextAreaLength from './components/MultiLanguageTextAreaLength/MultiLanguageTextAreaLength';
import { IMultiLanguageTextAreaProps } from './types';
import { localeStore } from 'src/mobx/localesStore';
import { isMultiLanguageValid } from '~/helpers';
import { LOCALES } from 'src/constants/main';
import './style.scss';

const MultiLanguageTextArea: FC<IMultiLanguageTextAreaProps> = ({
  title,
  enText,
  arText,
  onChangeValue,
  changeCurrentLanguageCallback,
  required,
  defaultEnText,
  defaultArText,
  showLength,
  placeholder,
  validation,
  titleTooltip,
  tooltip,
  disableLineBreak = false,
  className = '',
  textAreaClassName = '',
  headerClassName = '',
  titleClassName = '',
  //
  onChange,
  onInput,
  // eslint-disable-next-line
  value,
  // eslint-disable-next-line
  defaultValue,
  ...props
}: IMultiLanguageTextAreaProps): JSX.Element => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [currentLanguage, setCurrentLanguage] = useState<LOCALES>(localeStore.language === LOCALES.AR ? LOCALES.AR : LOCALES.EN);
  const [enValue, setEnValue] = useState<string | undefined>(enText || defaultEnText);
  const [arValue, setArValue] = useState<string | undefined>(arText || defaultArText);

  const isValid: boolean = isMultiLanguageValid(validation);
  const isCurrentEnLanguage = useMemo((): boolean => currentLanguage === LOCALES.EN, [currentLanguage]);
  const areaValue = useMemo((): string => (isCurrentEnLanguage ? enValue || '' : arValue || ''), [enValue, arValue, isCurrentEnLanguage]);

  useEffect(() => {
    if (changeCurrentLanguageCallback) changeCurrentLanguageCallback(currentLanguage);
  }, [changeCurrentLanguageCallback, currentLanguage]);

  useEffect(() => {
    if (enText !== null && enText !== enValue) setEnValue(enText);
    if (arText !== null && arText !== arValue) setArValue(arText);

    // WARNING !!! not add enValue or arValue in dependencies
  }, [enText, arText]);

  const changeCurrentLanguage = useCallback((buttonLocale?: LOCALES) => {
    if (buttonLocale) setCurrentLanguage(buttonLocale);
  }, []);

  const onTextChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      // @ts-ignore
      if (disableLineBreak && event.nativeEvent.inputType === 'insertLineBreak') return;

      const { value } = event.target;

      if (isCurrentEnLanguage) {
        setEnValue(value);
      } else {
        setArValue(value);
      }

      onChangeValue(value, currentLanguage);
    },
    [onChange, onInput, onChangeValue, currentLanguage, isCurrentEnLanguage]
  );

  const _textAreaWrapperClassName = useMemo((): string => {
    return classNames(
      'd-flex flex-column full-width full-height',
      { 'multi-lng-textarea_without-title': !title },
      { 'input-error': !title && !isValid }
    );
  }, [title, isValid]);

  const _textAreaClassName = useMemo((): string => {
    return classNames(
      'multi-lng-textarea__textarea scrollable-block__scroll_small letter-spacing-5',
      textAreaClassName,
      { 'input-error': !isValid },
      { 'multi-lng-textarea__textarea_disabled': props.disabled },
      { 'multi-lng-textarea__textarea_without-title': !title }
    );
  }, [textAreaClassName, isValid, props.disabled, title]);

  const renderTextArea = () => {
    return (
      <textarea
        {...props}
        ref={textAreaRef}
        placeholder={placeholder || localeStore.t('text')}
        value={areaValue}
        onChange={onTextChange}
        dir={isCurrentEnLanguage ? 'ltl' : 'rtl'}
        className={_textAreaClassName}
      />
    );
  };

  return (
    <div className={classNames('multi-lng-textarea', className)}>
      <div className={_textAreaWrapperClassName}>
        <MultiLanguageTextAreaHeader
          textAreaRef={textAreaRef}
          title={title}
          titleTooltip={titleTooltip}
          currentLanguage={currentLanguage}
          changeCurrentLanguage={changeCurrentLanguage}
          required={required}
          disabled={props.disabled}
          validation={validation}
          headerClassName={headerClassName}
          titleClassName={titleClassName}
        />
        {tooltip ? (
          <CustomTooltip
            triggerType={tooltip.triggerType}
            position={tooltip.position}
            trigger={tooltip.trigger}
            disableTrigger={tooltip.disableTrigger}
            permanentVisible={tooltip.permanentVisible}
            message={tooltip.message}
            duration={tooltip.duration}
            maxWidth={tooltip.maxWidth}
            className={tooltip.className}
            wrapperClassName={classNames('full-height full-width', tooltip.wrapperClassName)}
            contentClassName={tooltip.contentClassName}
            messageClassName={tooltip.messageClassName}
          >
            {renderTextArea()}
          </CustomTooltip>
        ) : (
          renderTextArea()
        )}
      </div>

      <MultiLanguageTextAreaLength value={areaValue} showLength={showLength} maxLength={props.maxLength} />

      <MultiLanguageInputErrorText isValid={isValid} currentLanguage={currentLanguage} validation={validation} />
    </div>
  );
};

export default MultiLanguageTextArea;
